import React from 'react';
import { Plus, MapPin } from 'lucide-react';
import { Button, PhoneInput, TextInput, Textarea, phone } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import {
  LIMITS, SOCIAL_KEYS, contactDraft, contactPayload, draftGeoPoint, geoDraftOf, saveStatus, overLimit,
} from '../MyPage.shared';
import { RowActions, SaveRow, moveItem, useFieldStyle } from '../parts/common';
import OfficeMap from '../parts/OfficeMap';

// «اطلاعات مطب» — the office facts the doctor types themself: phones, address,
// hours, an optional map point, socials. Deliberately NOT joined from clinic
// memberships (a practitioner has many clinics; the page states their own
// office, knowingly — design §5.1). The phone typed here is public.

const rowBox = { display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' };
const group = { border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '0.875rem', display: 'grid', gap: '0.75rem' };
const groupTitle = { fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary };

export default function OfficeEditor({ page, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const { draft, setDraft, dirty, adopt, reset } = useDraft(contactDraft(page.contact));
  const card = cards.office || {};

  const patchList = (list, idx, k, v) => setDraft((d) => ({
    ...d, [list]: d[list].map((row, i) => (i === idx ? { ...row, [k]: v } : row)),
  }));
  const removeFrom = (list) => (idx) => setDraft((d) => ({ ...d, [list]: d[list].filter((_, i) => i !== idx) }));
  const moveIn = (list) => (idx, delta) => setDraft((d) => ({ ...d, [list]: moveItem(d[list], idx, delta) }));

  const invalid = overLimit(draft.address, LIMITS.address_max) || overLimit(draft.city, LIMITS.city_max)
    || draft.phones.length > LIMITS.phones_max || draft.hours.length > LIMITS.hours_max;

  const save = async () => {
    const res = await actions.updatePage('office', { contact: contactPayload(draft) });
    if (res.ok) adopt(contactDraft(res.page?.contact));
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }} data-testid="my-page-office">
      <div style={{ fontSize: '0.8125rem', color: C.textSecondary }}>{s('myPage.office.intro')}</div>

      <div style={group}>
        <div style={groupTitle}>{s('myPage.office.phones')}</div>
        {draft.phones.map((p, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={rowBox} data-testid={`my-page-office-phone-${i}`}>
            <TextInput
              label={s('myPage.office.phoneLabel')}
              placeholder={s('myPage.office.phoneLabelPlaceholder')}
              value={p.label}
              onChange={(e) => patchList('phones', i, 'label', e.target.value)}
              maxLength={LIMITS.phone_label_max}
              disabled={readOnly}
              inputStyle={fieldStyle}
              style={{ flex: '1 1 8rem' }}
            />
            {/* The kit field (PM-0078): flag + country picker, any country. An office line
                is usually a landline, hence `allowLandline`. What is STORED is what the
                public page prints and dials — see `officeNumber`. */}
            <PhoneInput
              label={s('myPage.office.phoneNumber')}
              allowLandline
              value={p.number}
              onChange={(e164, meta) => patchList('phones', i, 'number', officeNumber(e164, meta))}
              disabled={readOnly}
              inputStyle={fieldStyle}
              style={{ flex: '1 1 12rem' }}
            />
            <RowActions index={i} count={draft.phones.length} disabled={readOnly} onMove={moveIn('phones')} onRemove={removeFrom('phones')} />
          </div>
        ))}
        {draft.phones.length < LIMITS.phones_max && !readOnly ? (
          <Button variant="outline" size="sm" className="justify-self-start" onClick={() => setDraft((d) => ({ ...d, phones: [...d.phones, { label: '', number: '' }] }))} data-testid="my-page-office-phone-add">
            <Plus size={16} aria-hidden /> {s('myPage.office.addPhone')}
          </Button>
        ) : null}
      </div>

      <div style={group}>
        <TextInput
          label={s('myPage.office.city')}
          value={draft.city}
          onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
          maxLength={LIMITS.city_max}
          disabled={readOnly}
          inputStyle={fieldStyle}
        />
        <Textarea
          label={s('myPage.office.address')}
          value={draft.address}
          onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
          rows={2}
          maxLength={LIMITS.address_max}
          showCount
          disabled={readOnly}
          inputStyle={fieldStyle}
          dir="auto"
        />
      </div>

      <div style={group}>
        <div style={groupTitle}>{s('myPage.office.hours')}</div>
        {draft.hours.map((h, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={rowBox} data-testid={`my-page-office-hours-${i}`}>
            <TextInput
              label={s('myPage.office.hoursLabel')}
              placeholder={s('myPage.office.hoursLabelPlaceholder')}
              value={h.label}
              onChange={(e) => patchList('hours', i, 'label', e.target.value)}
              maxLength={LIMITS.hours_label_max}
              disabled={readOnly}
              inputStyle={fieldStyle}
              style={{ flex: '1 1 8rem' }}
            />
            <TextInput
              label={s('myPage.office.hoursValue')}
              placeholder={s('myPage.office.hoursValuePlaceholder')}
              value={h.value}
              onChange={(e) => patchList('hours', i, 'value', e.target.value)}
              maxLength={LIMITS.hours_value_max}
              disabled={readOnly}
              inputStyle={fieldStyle}
              style={{ flex: '1 1 9rem' }}
            />
            <RowActions index={i} count={draft.hours.length} disabled={readOnly} onMove={moveIn('hours')} onRemove={removeFrom('hours')} />
          </div>
        ))}
        {draft.hours.length < LIMITS.hours_max && !readOnly ? (
          <Button variant="outline" size="sm" className="justify-self-start" onClick={() => setDraft((d) => ({ ...d, hours: [...d.hours, { label: '', value: '' }] }))} data-testid="my-page-office-hours-add">
            <Plus size={16} aria-hidden /> {s('myPage.office.addHours')}
          </Button>
        ) : null}
      </div>

      <div style={group}>
        <div style={{ ...groupTitle, display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
          <MapPin size={16} aria-hidden /> {s('myPage.office.geo')}
        </div>
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.office.geoHint')}</div>
        <OfficeMap
          value={draftGeoPoint(draft.geo)}
          onChange={(point) => setDraft((d) => ({ ...d, geo: geoDraftOf(point) }))}
          disabled={readOnly}
        />
      </div>

      <div style={group}>
        <div style={groupTitle}>{s('myPage.office.socials')}</div>
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.office.socialsHint')}</div>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 14rem), 1fr))' }}>
          {SOCIAL_KEYS.map((k) => (
            <TextInput
              key={k}
              label={s(`myPage.office.social.${k}`)}
              dir="ltr"
              value={draft.socials[k]}
              onChange={(e) => setDraft((d) => ({ ...d, socials: { ...d.socials, [k]: e.target.value } }))}
              maxLength={LIMITS.social_max}
              disabled={readOnly}
              inputStyle={fieldStyle}
              autoComplete="off"
            />
          ))}
        </div>
      </div>

      <SaveRow
        status={saveStatus({ dirty, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={card.error}
        onSave={save}
        onReset={reset}
        disabled={readOnly || invalid}
        testId="my-page-office"
      />
    </div>
  );
}

/**
 * The spelling an office number is STORED in, which is also the one the public page
 * prints verbatim (`landing … blocks.jsx` renders `number` as given): an Iranian number in
 * its national form, `02188000000`, because that is how an Iranian visitor reads and
 * dials it; any other country in E.164, because a national spelling without its country
 * is a different number. A number still being typed is kept as typed, so the service's
 * own check — not a silent drop of the row — is what answers it.
 */
function officeNumber(e164, meta) {
  if (!e164) return meta?.draft ?? '';
  return e164.startsWith('+98') ? phone.forDisplay(e164) : e164;
}
